import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { Link, LinkType, SnaphotReference } from '@/models'
import { IArtifactService } from '@/services/IArtifactService'
import { useBaseStore } from '.'
import { groupBy } from '@/utils/helpers'

/**
 * Link Cache Store
 *
 * Manages link data caching and link types.
 * Pure data management without UI state.
 */
export const useLinkCacheStore = defineStore('linkCacheStore', () => {
  // State
  const links = ref<Link[]>([])
  const linkTypes = ref<LinkType[]>([])

  // Getters
  const getLinksForArtifact = computed(() => {
    return (artifactId: number): Link[] => {
      return links.value.filter((l) => l.artifactAId === artifactId || l.artifactBId === artifactId)
    }
  })

  /** Map from artifactAId to all links where that artifact is the source (A side) */
  const linksWithA = computed(() => groupBy(links.value, (l) => l.artifactAId))

  /** Map from artifactBId to all links where that artifact is the target (B side) */
  const linksWithB = computed(() => groupBy(links.value, (l) => l.artifactBId))

  const getLinkById = computed(() => {
    return (id: number): Link | undefined => {
      return links.value.find((l) => l.id === id)
    }
  })

  const getLinkType = computed(() => {
    return (id: number): LinkType | undefined => {
      return linkTypes.value.find((lt) => lt.id === id)
    }
  })

  // Actions

  /**
   * Load links for a specific artifact
   */
  async function loadLinksForArtifact(params: {
    artifactService: IArtifactService
    projectId: number
    snapshotId: SnaphotReference
    artifactId: number
    filterId?: number
  }): Promise<Link[]> {
    const baseStore = useBaseStore()
    baseStore.startNewJob()

    try {
      const response = await params.artifactService.getLinkedArtifacts(
        params.projectId,
        params.snapshotId,
        params.artifactId,
        'any',
        params.filterId,
      )

      setLinks(response.links)
      return response.links
    } catch (error) {
      console.error('Error loading links:', error)
      return []
    } finally {
      baseStore.decrementRunningJobs()
    }
  }

  /**
   * Load link types for a snapshot
   */
  async function loadLinkTypes(params: {
    artifactService: IArtifactService
    projectId: number
    snapshotId: SnaphotReference
    filterId?: number
  }): Promise<void> {
    const baseStore = useBaseStore()
    baseStore.startNewJob()

    try {
      const types = await params.artifactService.getLinkTypes(
        params.projectId,
        params.snapshotId,
        params.filterId,
      )

      setLinkTypes(types)
    } catch (error) {
      console.error('Error loading link types:', error)
    } finally {
      baseStore.decrementRunningJobs()
    }
  }

  /**
   * Set links (replace all)
   */
  function setLinks(newLinks: Link[]): void {
    links.value = newLinks
  }

  /**
   * Update links (merge without duplicates)
   */
  function updateLinks(newLinks: Link[]): void {
    newLinks.forEach((newLink) => {
      const exists = links.value.some((link) => link.id === newLink.id)
      if (!exists) {
        links.value.push(newLink)
      }
    })
  }

  /**
   * Set link types (replace all)
   */
  function setLinkTypes(types: LinkType[]): void {
    linkTypes.value = types
  }

  /**
   * Update link types (merge without duplicates)
   */
  function updateLinkTypes(types: LinkType[]): void {
    types.forEach((newType) => {
      const exists = linkTypes.value.some((lt) => lt.id === newType.id)
      if (!exists) {
        linkTypes.value.push(newType)
      }
    })
  }

  /**
   * Clear all links
   */
  function clearLinks(): void {
    links.value = []
  }

  /**
   * Clear all link types
   */
  function clearLinkTypes(): void {
    linkTypes.value = []
  }

  /**
   * Clear all cache
   */
  function clearAll(): void {
    clearLinks()
    clearLinkTypes()
  }

  // Return public API
  return {
    // State
    links,
    linkTypes,
    // Getters
    getLinksForArtifact,
    linksWithA,
    linksWithB,
    getLinkById,
    getLinkType,
    // Actions
    loadLinksForArtifact,
    loadLinkTypes,
    setLinks,
    updateLinks,
    setLinkTypes,
    updateLinkTypes,
    clearLinks,
    clearLinkTypes,
    clearAll,
  }
})
